"use client";
import { useCurrency } from "@/app/providers/currencyContext";
import { GiftCard } from "@/dto/giftCard";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { giftCardRoute } from "@/utils/giftCardUtils";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next13-progressbar";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";

interface GiftCardCartItemProps {
  giftCard: GiftCard | null;
  // eslint-disable-next-line no-unused-vars
  onClick?: (event: React.MouseEvent) => void;
  onRequestClose?: () => void;
  showBottomBorder?: boolean;
}

export const GiftCardCartItem = ({
  giftCard,
  onClick,
  onRequestClose,
  showBottomBorder,
}: GiftCardCartItemProps) => {
  const router = useRouter();
  const t = useTranslations("GiftCard");
  const scanToOrderInfo = getFromLocalStorage<ScanToOrderInfo | null>(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  const handleGiftCardItemClick = () => {
    let queryParams = "";
    if (scanToOrderInfo) {
      queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
    }
    router.push(giftCardRoute + queryParams);
    if (onRequestClose) {
      onRequestClose();
    }
  };
  const { format } = useCurrency();

  return (
    <div
      className={`flex flex-col justify-between items-center py-4 cursor-pointer ${
        showBottomBorder ? "border-b" : ""
      }`}
      data-testid={applyDataTestId(pom.giftCard.giftCardCartItem)}
      onClick={handleGiftCardItemClick}
    >
      <div className="w-full flex flex-row justify-between">
        <div className="w-full flex flex-col justify-between">
          <p className="text-lg text-[#373737] max-w-[310px]">
            {t("giftCard")}
          </p>
          <div className="max-w-[80%]">
            {!giftCard?.recipient?.isCustomer && (
              <ul className="list-disc ml-6 text-[15px] text-[#727272] space-y-1">
                {giftCard?.recipient?.name && (
                  <li>{giftCard?.recipient?.name}</li>
                )}
                {giftCard?.recipient?.email && (
                  <li>{giftCard?.recipient.email}</li>
                )}
                {giftCard?.note && (
                  <li className="max-w-[310px]">
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {giftCard?.note}
                    </div>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-start items-end">
          <p className="text-[16px] font-medium mt-0.5">
            {giftCard?.amount && format(giftCard?.amount * 100)}
          </p>
          <button
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              if (onClick) onClick(event);
            }}
            className="mt-2"
          >
            <TrashIcon width={14} height={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
